import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Server from '../../../config/Server';
import { IState } from '../../../config/rootReducer';
import { IHomeAccess } from '../reducer/HomeReducer';
import { HomeAccessActions } from './HomeAccessActionTypes';
import {
  BI_URL,
  DASHBOARD_URL,
  INTEGRATION_DASHBOARD_URL,
  INTEGRATION_URL,
  LOGOUT_REDIRECT,
  METADATA_SERVICE,
  PROJECT_URL,
} from '../../../config/envVariables';
import { LogOutAction } from '../../Login/actions/LoginActions';
import { RedirectToLogin } from '@wm/login-redirect-library';

export const PRODUCT_URLS = {
  dashboard: DASHBOARD_URL,
  project_manager: PROJECT_URL,
  integration_manager: INTEGRATION_URL,
  bi: BI_URL,
  integration_dashboard: INTEGRATION_DASHBOARD_URL,
};

export const ONE_ACCESS = {
  dashboard: false,
  project_manager: false,
  integration_manager: true,
  bi: false,
};

interface IServerMessage {
  readonly code: string;
}

/**
 * Acción que cambia el acceso a los productos de WM
 * @param access Objeto que contiene los accesos a los productos
 * @returns Acción receptada por el reducer
 */
const HomeAccessAction = (access: IHomeAccess) => ({
  type: HomeAccessActions.Access,
  access,
});

/**
 * Objeto que cambia el valor del error en el estado
 * @param error Booleano que indica si hay error o no
 * @returns Objeto receptado por el reducer
 */
const HomeAccessError = (error: boolean) => ({
  type: HomeAccessActions.Error,
  error,
});

/**
 * Acción que muestra la página de carga
 * @param load Booleano que indica si se está cargando info o no
 * @returns Objeto receptado por el reducer
 */
const HomeAccessLoadingScreen = (load: boolean) => ({
  type: HomeAccessActions.Load,
  load,
});

/**
 * Objeto que cambia el valor del mensaje de error
 * @param message Mensaje recibido por el usuario
 * @returns Objeto receptado por el reducer
 */
const HomeAccessErrorMessage = (message: string) => ({
  type: HomeAccessActions.Error,
  message,
});

/**
 * Función que busca al servicio de metadata los accesos a los productos
 * @param initial Booleano que indica si es carga inicial o no
 */
export const DispatchAccess = (initial: boolean) => (
  dispatch: ThunkDispatch<IState, void, AnyAction>,
  getState: () => IState
) => {
  const state = getState();
  dispatch(HomeAccessLoadingScreen(true));
  if (state.home.error) dispatch(HomeAccessError(false));
  const userId = state.profile.user.id;

  Server.get(`${METADATA_SERVICE}/users/access`)()
    .catch((code: IServerMessage) => dispatch(DispatchError(code.code)))
    .then((response: IHomeAccess) => dispatch(HomeAccessAction(response)))
    .then(() => dispatch(checkCurrentAccess()));
  
};

/**
 * Función que verifica si el usuario tiene acceso a un solo producto.
 * Si se da la ocasión, se debe redirigir al usuario al único producto que
 * tiene asignado.
 */
const checkCurrentAccess = () => (
  dispatch: ThunkDispatch<IState, void, AnyAction>,
  getState: () => IState
) => {
  const state = getState();
  const { access } = state.home;  
  const numberOfAccesses: string[] = [];
  
  if (access !== undefined) {
    Object.keys(access).forEach(products => {
      if (access[products]) numberOfAccesses.push(products);
    });
  
    if (numberOfAccesses.length === 1) {
      window.location.replace(PRODUCT_URLS[numberOfAccesses[0]]);
    } else {
      dispatch(HomeAccessLoadingScreen(false));
    }
  } else {   
    dispatch(LogOutAction());
    RedirectToLogin(`${LOGOUT_REDIRECT}/close`, []);
  }
  
};

/**
 * Función disparada cuando la llamada para obtener los accesos falla
 * @param code Codigo de error recibido por la aplicacion
 * @returns Mensajes de error
 */
const DispatchError = (code: string) => (dispatch: ThunkDispatch<IState, void, AnyAction>) => {
  dispatch(HomeAccessError(true));
  dispatch(HomeAccessErrorMessage(code));
};
